/**
 * Scheduling helpers for the create-post "Schedule for Later" flow.
 *
 * Wall-clock <-> UTC conversion honours a user-SELECTED IANA timezone using
 * only native Intl.DateTimeFormat (no date library). This matters because the
 * shared DateTimePicker round-trips values through the browser zone, which is
 * wrong the moment the user picks a different zone in the timezone dropdown.
 * The selected zone is UI-only state — never persisted; only the converted UTC
 * `scheduled_at` instant is sent to the backend.
 */

/** Minimum lead time (minutes) a scheduled post must be in the future. */
export const MIN_SCHEDULE_LEAD_MINUTES = 5;

/** Wall-clock shape `YYYY-MM-DDTHH:mm` (the datetime-local input format). */
const WALL_CLOCK_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/;

/** Left-pad a number to two digits (date/time formatting). */
const pad = (n: number): string => n.toString().padStart(2, "0");

/**
 * The browser's IANA timezone (e.g. "America/New_York"), used to pre-select
 * the timezone dropdown.
 */
export function getBrowserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * The full IANA timezone list for the dropdown, guaranteed to include the
 * browser zone (older engines without Intl.supportedValuesOf fall back to
 * just the browser zone + UTC).
 */
export function listTimeZones(): string[] {
  const zones =
    typeof Intl.supportedValuesOf === "function"
      ? Intl.supportedValuesOf("timeZone")
      : ["UTC"];
  const browserZone = getBrowserTimeZone();
  return zones.includes(browserZone) ? zones : [browserZone, ...zones];
}

/**
 * Read a Date's wall-clock components as seen in the given IANA zone.
 *
 * @param date - the instant to project
 * @param timeZone - IANA zone name
 * @returns numeric year/month/day/hour/minute/second parts in that zone
 */
function partsInZone(
  date: Date,
  timeZone: string,
): {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
} {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  const parts: Record<string, string> = {};
  for (const part of formatter.formatToParts(date)) {
    parts[part.type] = part.value;
  }
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour),
    minute: Number(parts.minute),
    second: Number(parts.second),
  };
}

/**
 * The zone's UTC offset (ms) at a given instant: how far the zone's wall
 * clock is ahead of UTC (negative when behind).
 *
 * @param date - the instant to measure at (DST makes this instant-dependent)
 * @param timeZone - IANA zone name
 */
function zoneOffsetMs(date: Date, timeZone: string): number {
  const p = partsInZone(date, timeZone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  // Compare at whole-second precision (formatToParts drops milliseconds).
  return asUtc - Math.floor(date.getTime() / 1000) * 1000;
}

/**
 * Convert a wall-clock time in a selected IANA zone to a UTC ISO 8601 instant.
 *
 * Two-pass offset resolution: the first pass guesses using the offset at the
 * naive-UTC reading of the wall clock; the second re-reads the offset at the
 * corrected instant so times on either side of a DST transition resolve with
 * the offset actually in force.
 *
 * @param wallClock - `YYYY-MM-DDTHH:mm` as typed/displayed in the picker
 * @param timeZone - the SELECTED IANA zone the wall clock is expressed in
 * @returns UTC ISO string (e.g. `"2026-07-15T16:00:00.000Z"`), or null for
 *   unparseable input
 */
export function wallClockToUtcIso(
  wallClock: string,
  timeZone: string,
): string | null {
  const match = WALL_CLOCK_PATTERN.exec(wallClock);
  if (!match) return null;
  const [, year, month, day, hour, minute] = match.map(Number);

  const naiveUtc = Date.UTC(year, month - 1, day, hour, minute);
  const firstPass = zoneOffsetMs(new Date(naiveUtc), timeZone);
  const secondPass = zoneOffsetMs(new Date(naiveUtc - firstPass), timeZone);
  return new Date(naiveUtc - secondPass).toISOString();
}

/**
 * Project a UTC ISO instant onto a zone's wall clock (`YYYY-MM-DDTHH:mm`),
 * e.g. to re-display a stored `scheduled_at` in the selected zone.
 *
 * @param iso - UTC ISO 8601 string
 * @param timeZone - IANA zone to display in
 * @returns wall-clock string, or null for unparseable input
 */
export function utcIsoToWallClock(
  iso: string,
  timeZone: string,
): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const p = partsInZone(date, timeZone);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

/**
 * Validate a scheduled UTC instant against the minimum lead time.
 *
 * @param scheduledAtIso - the UTC `scheduled_at` instant (or null when unset)
 * @param now - injectable clock for tests; defaults to the current time
 * @returns a user-facing error message, or null when the time is valid
 */
export function scheduledAtError(
  scheduledAtIso: string | null,
  now: Date = new Date(),
): string | null {
  if (!scheduledAtIso) {
    return "Choose a date and time to schedule this post.";
  }
  const scheduledMs = new Date(scheduledAtIso).getTime();
  if (Number.isNaN(scheduledMs)) {
    return "Choose a date and time to schedule this post.";
  }
  if (scheduledMs < now.getTime() + MIN_SCHEDULE_LEAD_MINUTES * 60_000) {
    return `Scheduled time must be at least ${MIN_SCHEDULE_LEAD_MINUTES} minutes in the future.`;
  }
  return null;
}
